/**
 * When user is logged in (customer token), fetches GET /auth/me on mount and every 60s
 * so has_company and is_admin stay in sync without re-login.
 * Applies user's preferred locale from /me to i18n so UI switches to their language after login.
 */
import { useEffect, useRef } from 'react'
import { unifiedAuth } from '../../api/authClient'
import { useAuthStore } from 'shared/store/authStore'
import i18n from 'shared/i18n'

const ME_REFRESH_MS = 60_000

/** Backend uses bhs for Bosnian, frontend uses bs. */
function backendLocaleToFrontend(locale: string): string {
  const code = (locale || '').toLowerCase()
  return code === 'bhs' ? 'bs' : code || 'bs'
}

export default function AuthMeSync() {
  const customer = useAuthStore((s) => s.customer)
  const setMe = useAuthStore((s) => s.setMe)
  const logoutCustomer = useAuthStore((s) => s.logoutCustomer)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (!customer?.token) {
      setMe(null)
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      return
    }

    const fetchMe = () => {
      unifiedAuth
        .me()
        .then(({ data }) => {
          const locale = data.locale ?? 'bhs'
          setMe({
            has_company: data.has_company,
            is_admin: data.is_admin,
            company_id: data.company_id ?? null,
            company_name: data.company_name ?? null,
            locale,
          })
          const frontendLng = backendLocaleToFrontend(locale)
          if (i18n.language && (i18n.language.split('-')[0] !== frontendLng)) {
            i18n.changeLanguage(frontendLng)
          }
        })
        .catch((err) => {
          const status = err?.response?.status
          if (status === 401) {
            setMe(null)
            logoutCustomer()
          }
        })
    }

    fetchMe()
    intervalRef.current = setInterval(fetchMe, ME_REFRESH_MS)
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [customer?.token, setMe, logoutCustomer])

  return null
}
